import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideRouter, withHashLocation } from '@angular/router'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import Aura from '@primeng/themes/aura'
import { SimpleNotificationsModule } from 'angular2-notifications'
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular'
import { InlineSVGModule } from 'ng-inline-svg-2'
import { ConfirmationService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { catchError, firstValueFrom, Observable } from 'rxjs'
import { routes } from './app.routes'
import { ConfigService } from './shared/services/config.service'
import { SpinnerService } from './shared/services/spinner/spinner.service'
import { FeatureEPEffects } from './shared/store/feature-edit-project/feature-ep.effects'
import { featureEPReducer } from './shared/store/feature-edit-project/feature-ep.reducer'
import { FeatureMYDEffects } from './shared/store/feature-manage-your-drafts/feature-myd.effects'
import { featureMYDReducer } from './shared/store/feature-manage-your-drafts/feature-myd.reducer'
import { FeatureMYPEffects } from './shared/store/feature-manage-your-projects/feature-myp.effects'
import { featureMYPReducer } from './shared/store/feature-manage-your-projects/feature-myp.reducer'
import { FeaturePDEffects } from './shared/store/feature-project-detail/feature-pd.effects'
import { featurePDReducer } from './shared/store/feature-project-detail/feature-pd.reducer'
import { FeatureSPEffects } from './shared/store/feature-search-projects/feature-sp.effects'
import { featureSPReducer } from './shared/store/feature-search-projects/feature-sp.reducer'
import { handlerReducer } from './shared/store/handler/handler.reducer'
import { StageEffects } from './shared/store/stage/stage.effects'
import { stageReducer } from './shared/store/stage/stage.reducer'

export const appConfig: ApplicationConfig = {
   providers: [
      provideAnimationsAsync(),
      providePrimeNG({
         theme: {
            preset: Aura,
            options: {
               darkModeSelector: false || 'none',
               cssLayer: {
                  name: 'primeng',
                  order: 'tailwind-base, tailwind-utilities, primeng',
               },
            },
         },
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes, withHashLocation()),
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      DialogService,
      SpinnerService,
      {
         provide: DynamicDialogRef,
         useValue: [],
      },
      TranslateModule.forRoot({
         defaultLanguage: 'en-US',
         useDefaultLang: true,
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpBackend],
         },
      }).providers!,
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [TranslateService],
         useFactory: initializeTranslation,
      },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [ConfigService],
         useFactory: (configService: ConfigService) => {
            return () => configService.initRuntimeConfig()
         },
      },
      {
         provide: APP_INITIALIZER,
         useFactory: initializeKeycloak,
         multi: true,
         deps: [KeycloakService, HttpClient],
      },
      KeycloakService,
      ConfirmationService,
      { provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true },
      importProvidersFrom(
         InlineSVGModule.forRoot(),
         StoreModule.forRoot({
            stage: stageReducer,
            featureSearchProjects: featureSPReducer,
            handler: handlerReducer,
            featureProjectDetail: featurePDReducer,
            featureEditProject: featureEPReducer,
            featureManageYourProjects: featureMYPReducer,
            featureManageYourDrafts: featureMYDReducer,
         }),
         EffectsModule.forRoot([StageEffects, FeatureSPEffects, FeaturePDEffects, FeatureEPEffects, FeatureMYPEffects, FeatureMYDEffects]),
         StoreDevtoolsModule.instrument({
            maxAge: 25,
         }),
         SimpleNotificationsModule.forRoot({
            timeOut: 5000,
            showProgressBar: true,
            pauseOnHover: true,
            clickToClose: true,
            position: ['bottom', 'right'],
            lastOnBottom: true,
            maxStack: 3,
            maxLength: 500,
            theClass: 'app-notify',
            preventDuplicates: true,
         })
      ),
   ],
}
// C.L. Workaround for the translation library. There is a bug when using a different base-href
// https://github.com/ngx-translate/http-loader/issues/11#issuecomment-329292083
export function HttpLoaderFactory(handler: HttpBackend) {
   return new CustomTranslateHttpLoader('en-US', new HttpClient(handler), './assets/i18n/')
}

/*
* C.L. Workaround for the translation library.

 For some reason, the library randomly fails in falling back to the default locale.
 This workaround forces the application to load the default, if the requested locale is not found
 https://github.com/ngx-translate/core/issues/1002#issuecomment-827552949
 *
 * The bug is reproducible on dev environment, by forcing a 'slow' network on the dev tools.
 *
 * I suppose that on a slow network, the application starts before the translation files have been received
 * and is then unable to execute the fallback logic.
 *
 * Probably, the better way to fix this, is to configure the missingTranslationHandler provided by the library
 * https://ngx-translate.org/reference/missing-translation-handler-api/
 */
export class CustomTranslateHttpLoader extends TranslateHttpLoader {
   defaultLocale: string

   constructor(defaultLocale: string, http: HttpClient, prefix?: string, suffix?: string) {
      super(http, prefix, suffix)
      this.defaultLocale = defaultLocale
   }

   override getTranslation(lang: string): Observable<Object> {
      return super.getTranslation(lang).pipe(
         catchError((err) => {
            console.log(`Locale ${lang} is not supported, using default ${this.defaultLocale}`)
            return super.getTranslation(this.defaultLocale)
         })
      )
   }
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      // Usa en-US se la lingua del browser non è supportata
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return firstValueFrom(translate.use(langToUse))
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return () => {
      return firstValueFrom(httpClient.get<KeycloakConfig>(`assets/configs/properties.json?ts=${Date.now()}`)).then((kc) => {
         return keycloak.init({
            config: {
               url: kc.keycloak.authUrl,
               realm: kc.keycloak.realm,
               clientId: kc.keycloak.clientId,
            },
            initOptions: { enableLogging: true, checkLoginIframe: false, flow: 'standard', onLoad: 'check-sso' },
            enableBearerInterceptor: true,
            bearerPrefix: 'Bearer ',
         })
      })
   }
}

interface KeycloakConfig {
   keycloak: {
      authUrl: string
      realm: string
      clientId: string
   }
}
